import * as React from 'react';
import { useRouter } from '@tanstack/react-router';

import { __internal as guestTokenInternal } from '~/hooks/useGuestToken';
import { onGuestSessionExpired } from '~/lib/api';

interface SessionExpiredNoticeProps {
  /** Landing-page slug — used to navigate back to the funnel start. */
  slug: string;
}

/**
 * Listens for guest-session expiry signals from the API client. When fired,
 * clears any stored session state, navigates back to the landing page, and
 * shows a one-line banner explaining the restart. Dismisses on user action
 * or after a short delay.
 */
export function SessionExpiredNotice({ slug }: SessionExpiredNoticeProps) {
  const router = useRouter();
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const unsubscribe = onGuestSessionExpired(() => {
      guestTokenInternal.clearStoredSession();
      setVisible(true);
      router.navigate({ to: '/$slug', params: { slug } });
    });
    return unsubscribe;
  }, [router, slug]);

  React.useEffect(() => {
    if (!visible) return;
    const timer = window.setTimeout(() => setVisible(false), 6000);
    return () => window.clearTimeout(timer);
  }, [visible]);

  if (!visible) return null;

  return <SessionExpiredBanner onDismiss={() => setVisible(false)} />;
}

SessionExpiredNotice.displayName = 'SessionExpiredNotice';

/**
 * Global variant of `SessionExpiredNotice` — mounted once at the route tree
 * root so every path that fires `apiClientWithGuestAuth` (including the splat
 * route at `/$.tsx`) has a listener. Derives the slug to navigate back to
 * from the current URL pathname.
 */
export function GlobalSessionExpiredNotice() {
  const router = useRouter();
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const unsubscribe = onGuestSessionExpired(() => {
      guestTokenInternal.clearStoredSession();
      setVisible(true);
      // Best-effort navigation back to the funnel start. The first path
      // segment is treated as the slug under both the `/$slug/*` and `/$`
      // (composite-slug) trees; if it can't be derived (e.g. already at `/`)
      // we just surface the banner without navigating.
      if (typeof window === 'undefined') return;
      const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
      if (firstSegment) {
        router.navigate({ to: '/$slug', params: { slug: firstSegment } });
      }
    });
    return unsubscribe;
  }, [router]);

  React.useEffect(() => {
    if (!visible) return;
    const timer = window.setTimeout(() => setVisible(false), 6000);
    return () => window.clearTimeout(timer);
  }, [visible]);

  if (!visible) return null;
  return <SessionExpiredBanner onDismiss={() => setVisible(false)} />;
}

GlobalSessionExpiredNotice.displayName = 'GlobalSessionExpiredNotice';

interface SessionExpiredBannerProps {
  onDismiss: () => void;
}

function SessionExpiredBanner({ onDismiss }: SessionExpiredBannerProps) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed top-[max(0.5rem,env(safe-area-inset-top,0px))] left-1/2 -translate-x-1/2 z-[100] px-4 py-2 rounded-md bg-amber-50 border border-amber-300 text-amber-900 shadow-md text-sm flex items-center gap-3"
    >
      <span>Your session expired — please play again.</span>
      <button
        type="button"
        onClick={onDismiss}
        className="text-amber-900/70 hover:text-amber-900 font-semibold"
        aria-label="Dismiss notice"
      >
        Dismiss
      </button>
    </div>
  );
}
